import type { ReactNode } from "react"

import credibilityBbb from "~/assets/photo/battery-only-proposal/credibility-bbb.png"
import credibilityBcorp from "~/assets/photo/battery-only-proposal/credibility-bcorp.png"
import credibilityInc500 from "~/assets/photo/battery-only-proposal/credibility-inc500.png"
import homeownerApp from "~/assets/photo/battery-only-proposal/homeowner-app.png"

import {
  Disclosure,
  Eyebrow,
  Icon,
  Panel,
  Section,
  SectionHeading,
} from "../BatteryOnlyProposal/primitives"
import { useDesktopViewport } from "../BatteryOnlyProposal/use-desktop-viewport"

const CREDIBILITY_ASSETS = [
  { alt: `BBB Accredited`, src: credibilityBbb, className: `h-[84px]` },
  { alt: `Certified B Corp`, src: credibilityBcorp, className: `h-[96px]` },
  { alt: `Inc. 500 — #61`, src: credibilityInc500, className: `h-[84px]` },
] as const

const APP_FEATURES = [
  {
    icon: `sparkle` as const,
    title: `Ask Powur AI Anything`,
    body: `Get instant answers about your projects, status, documents, and support — all in one place.`,
  },
  {
    icon: `doc` as const,
    title: `Manage Your Documents`,
    body: `View, upload, and download files — and quickly see what still needs your attention.`,
  },
  {
    icon: `check` as const,
    title: `Track Your Project in Real Time`,
    body: `Follow every stage of your installation, with clear milestones and what's coming next.`,
  },
  {
    icon: `help` as const,
    title: `Get Help When You Need It`,
    body: `Access live support directly in the app during business hours.`,
  },
  {
    icon: `ticket` as const,
    title: `Stay on Top of Requests`,
    body: `View existing tickets or create new ones — all in one streamlined system.`,
  },
  {
    icon: `chart` as const,
    title: `Monitor Your Energy & Savings`,
    body: `See your energy use and track long-term savings in real time.`,
  },
]

const STORE_BADGES = [
  {
    platform: `apple` as const,
    line1: `Download on the`,
    line2: `App Store`,
  },
  {
    platform: `google` as const,
    line1: `Get it on`,
    line2: `Google Play`,
  },
] as const

function StoreBadgesRow(): JSX.Element {
  return (
    <div className="flex flex-wrap gap-2.5">
      {STORE_BADGES.map((badge) => (
        <StoreBadge key={badge.platform} badge={badge} />
      ))}
    </div>
  )
}

function HomeownerAppMobile(): JSX.Element {
  return (
    <>
      <div className="mb-2 flex items-center gap-4">
        <img
          src={homeownerApp}
          alt="Powur homeowner app"
          loading="lazy"
          decoding="async"
          className="h-36 w-auto object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.5)]"
        />
        <div className="flex-1">
          <Eyebrow className="mb-2 text-[11px] tracking-[0.12em]">
            Available on iOS &amp; Android
          </Eyebrow>
          <StoreBadgesRow />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {APP_FEATURES.map((feature, index) => (
          <Disclosure
            key={feature.title}
            defaultOpen={index === 0}
            summary={
              <span className="flex items-center gap-3">
                <span className="grid size-8 place-items-center rounded-md border border-bp-border-strong bg-bp-elevated-2 text-bp-accent">
                  <Icon name={feature.icon} className="size-4" />
                </span>
                <span className="font-display text-[length:var(--step--1)] font-semibold tracking-[-0.015em] text-bp-text">
                  {feature.title}
                </span>
              </span>
            }
          >
            <p className="text-[length:var(--step--1)] leading-relaxed text-bp-text-muted">
              {feature.body}
            </p>
          </Disclosure>
        ))}
      </div>
      <Panel className="mt-5 flex flex-wrap items-center justify-around gap-4 px-5 py-4">
        {CREDIBILITY_ASSETS.map((asset) => (
          <img
            key={asset.alt}
            src={asset.src}
            alt={asset.alt}
            loading="lazy"
            decoding="async"
            className="h-14 w-auto max-w-[110px] object-contain"
          />
        ))}
      </Panel>
    </>
  )
}

/**
 * The homeowner app section both proposal kinds share: app image and feature
 * list, store badges, and the "Trusted & recognized" logo strip. Only the
 * heading differs by kind; everything else is product-neutral.
 */
export function HomeownerApp({
  heading,
}: {
  /** The line under the eyebrow, including its `GradText`. */
  heading: ReactNode
}): JSX.Element {
  const isDesktop = useDesktopViewport()

  if (!isDesktop) {
    return (
      <Section id="homeowner-app">
        <SectionHeading
          eyebrow="Power at your fingertips · Homeowner app"
          sub="Everything you need to manage your system, projects, and support — all in one app."
        >
          {heading}
        </SectionHeading>
        <HomeownerAppMobile />
      </Section>
    )
  }

  return (
    <Section id="homeowner-app">
      <SectionHeading
        eyebrow="Power at your fingertips · Homeowner app"
        sub="Everything you need to manage your system, projects, and support — all in one app."
      >
        {heading}
      </SectionHeading>
      <Panel className="relative grid min-w-0 items-center gap-14 p-8 lg:grid-cols-2 lg:py-14 lg:pr-14 lg:pl-0">
        <div className="relative flex min-h-0 min-w-0 items-center justify-start lg:min-h-[760px] lg:pl-14">
          <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-app-glow)]" />
          <img
            src={homeownerApp}
            alt="Powur homeowner app"
            loading="lazy"
            decoding="async"
            className="relative mx-auto max-h-[860px] w-auto max-w-full min-w-0 object-contain drop-shadow-[0_50px_80px_rgba(0,0,0,0.6)] lg:mx-0"
          />
        </div>
        <div className="relative min-w-0">
          <div className="grid gap-x-7 gap-y-7 sm:grid-cols-2">
            {APP_FEATURES.map((feature) => (
              <div key={feature.title} className="flex flex-col gap-2.5">
                <div className="grid size-11 place-items-center rounded-md border border-bp-border-strong bg-bp-elevated-2 text-bp-accent">
                  <Icon name={feature.icon} className="size-5" />
                </div>
                <div className="font-display text-[length:var(--step-0)] font-semibold tracking-[-0.015em] text-bp-text">
                  {feature.title}
                </div>
                <div className="text-[length:var(--step--1)] leading-relaxed text-bp-text-muted">
                  {feature.body}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-bp-border pt-[26px]">
            <Eyebrow className="text-[11px] tracking-[0.12em]">
              Available on iOS &amp; Android
            </Eyebrow>
            <StoreBadgesRow />
          </div>
        </div>
      </Panel>
      <Panel className="mt-7 grid items-center gap-9 px-9 py-7 lg:grid-cols-[auto_1fr]">
        <div>
          <Eyebrow className="mb-1.5">Trusted &amp; recognized</Eyebrow>
          <div className="font-display max-w-[240px] text-[length:var(--step-0)] leading-tight font-semibold tracking-[-0.015em] text-bp-text">
            We influence change.
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-around gap-7">
          {CREDIBILITY_ASSETS.map((asset) => (
            <img
              key={asset.alt}
              src={asset.src}
              alt={asset.alt}
              loading="lazy"
              decoding="async"
              className={`${asset.className} w-auto max-w-[160px] object-contain`}
            />
          ))}
        </div>
      </Panel>
    </Section>
  )
}

const STORE_MARKS = {
  apple: {
    width: 20,
    height: 22,
    path: `M17.5 12.5c0-3 2.4-4.4 2.5-4.5-1.4-2-3.5-2.3-4.3-2.3-1.8-.2-3.5 1.1-4.5 1.1s-2.4-1-3.9-1c-2 0-3.9 1.2-4.9 3-2.1 3.6-.5 9 1.5 11.9 1 1.4 2.2 3 3.8 3 1.5-.1 2.1-1 3.9-1 1.8 0 2.4 1 4 .9 1.7 0 2.7-1.5 3.7-2.9 1.2-1.7 1.7-3.3 1.7-3.4-.1-.1-3.5-1.4-3.5-5.3zM14.7 4c.8-1 1.4-2.4 1.2-3.7-1.2 0-2.6.8-3.4 1.8-.8.9-1.4 2.3-1.3 3.6 1.3.1 2.7-.7 3.5-1.7z`,
  },
  google: {
    width: 18,
    height: 20,
    path: `M3 20.5V3.5c0-.59.34-1.11.84-1.35L13.69 12 3.84 21.85C3.34 21.6 3 21.09 3 20.5m13.81-5.38L6.05 21.34 14.54 12.85l2.27 2.27m3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18L17.89 14.5 15.39 12l2.5-2.5 2.27 1.31M6.05 2.66l10.76 6.22-2.27 2.27L6.05 2.66z`,
  },
} as const

function StoreBadge({
  badge,
}: {
  badge: (typeof STORE_BADGES)[number]
}): JSX.Element {
  const mark = STORE_MARKS[badge.platform]

  return (
    <div className="inline-flex items-center gap-2.5 rounded-xl border border-bp-border-strong bg-bp-elevated px-4 py-[9px] text-bp-text">
      <svg
        width={mark.width}
        height={mark.height}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d={mark.path} />
      </svg>
      <div className="text-left leading-[1.1]">
        <div className="text-[8.5px] tracking-[0.06em] text-bp-text-muted">
          {badge.line1}
        </div>
        <div className="font-display mt-px text-sm font-semibold tracking-[-0.01em]">
          {badge.line2}
        </div>
      </div>
    </div>
  )
}
